const DB_NAME = 'pixie-outbound';
const STORE = 'items';
const DB_VERSION = 1;
const OUTBOUND_HOLD_TEXT =
  'Saved on this phone. It will send when the network is back.';

function isNetworkSendFailure(error, online = true) {
  if (online === false) return true;
  if (!error) return false;
  if (!error.response) {
    const code = String(error.code || '');
    if (
      code === 'ERR_NETWORK' ||
      code === 'ECONNABORTED' ||
      code === 'ERR_INTERNET_DISCONNECTED'
    ) {
      return true;
    }
    const message = String(error.message || '');
    return (
      /network error/i.test(message) ||
      /timeout/i.test(message) ||
      /failed to fetch/i.test(message)
    );
  }
  const status = Number(error.response.status);
  return status === 0 || status === 502 || status === 503 || status === 504;
}

function newOutboundId(now = Date.now()) {
  return `ob_${now}_${Math.random().toString(36).slice(2, 10)}`;
}

function normalizeOutbound(item, now = Date.now()) {
  const source = item || {};
  return {
    id: source.id || newOutboundId(now),
    kind: source.kind,
    createdAt: source.createdAt || now,
    failures: source.failures || 0,
    title: source.title || '',
    filename: source.filename || 'recording.ogg',
    attachedTo:
      source.attachedTo == null || source.attachedTo === ''
        ? null
        : String(source.attachedTo),
    attachedId:
      source.attachedId == null || source.attachedId === ''
        ? null
        : String(source.attachedId),
    audio: source.audio || null,
    audioMime: source.audioMime || (source.audio && source.audio.type) || '',
    text: source.text || '',
    isReply: !!source.isReply,
    isReplyTo: source.isReplyTo == null ? null : source.isReplyTo,
    fromUserId: source.fromUserId == null ? null : source.fromUserId,
    toUserId: source.toUserId == null ? null : source.toUserId,
    toPeopleId: source.toPeopleId == null ? null : source.toPeopleId,
    toGroupId: source.toGroupId == null ? null : source.toGroupId,
    conversationId:
      source.conversationId == null ? null : source.conversationId,
    peopleId: source.peopleId == null ? null : source.peopleId,
    isTenant: !!source.isTenant,
    datetime: source.datetime || new Date(now).toISOString(),
    images: Array.isArray(source.images) ? source.images : [],
    messageId: source.messageId == null ? null : source.messageId,
    imageIndex: Number(source.imageIndex) || 0,
    replaceVoiceNoteId:
      source.replaceVoiceNoteId == null || source.replaceVoiceNoteId === ''
        ? null
        : String(source.replaceVoiceNoteId),
  };
}

function insideChatOutboundItem(fields = {}) {
  const images = (fields.images || []).map((image) => ({
    blob: image.blob || image.file || null,
    name: image.name || (image.file && image.file.name) || 'photo.jpg',
    type: image.type || (image.file && image.file.type) || 'image/jpeg',
    isHeic: !!image.isHeic,
  }));
  return normalizeOutbound({
    kind: 'inside-chat',
    text: fields.text || '',
    isReply: fields.isReply,
    isReplyTo: fields.isReplyTo,
    fromUserId: fields.fromUserId,
    toUserId: fields.toUserId,
    toPeopleId: fields.toPeopleId,
    toGroupId: fields.toGroupId,
    conversationId: fields.conversationId,
    peopleId: fields.peopleId,
    isTenant: fields.isTenant,
    datetime: fields.datetime,
    audio: fields.audio || null,
    audioMime: fields.audioMime,
    images,
    messageId: fields.messageId,
    imageIndex: fields.imageIndex,
    createdAt: fields.createdAt,
    id: fields.id,
  });
}

function memoryOutboundStore() {
  const rows = new Map();
  return {
    async put(item) {
      rows.set(item.id, item);
      return item;
    },
    async all() {
      return [...rows.values()];
    },
    async delete(id) {
      rows.delete(id);
    },
  };
}

function openOutboundDb() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function idbOutboundStore() {
  const ready = openOutboundDb();
  const run = (mode, fn) =>
    ready.then(
      (db) =>
        new Promise((resolve, reject) => {
          const tx = db.transaction(STORE, mode);
          const request = fn(tx.objectStore(STORE));
          let result;
          request.onsuccess = () => {
            result = request.result;
          };
          request.onerror = () => reject(request.error);
          tx.oncomplete = () => resolve(result);
          tx.onerror = () => reject(tx.error);
          tx.onabort = () =>
            reject(tx.error || new Error('outbound store aborted'));
        }),
    );
  return {
    put(item) {
      return run('readwrite', (store) => store.put(item));
    },
    all() {
      return run('readonly', (store) => store.getAll()).then(
        (rows) => rows || [],
      );
    },
    delete(id) {
      return run('readwrite', (store) => store.delete(id));
    },
  };
}

let activeStore = null;
let changeListener = null;

function outboundStore() {
  if (activeStore) return activeStore;
  if (typeof indexedDB === 'undefined') {
    activeStore = memoryOutboundStore();
    return activeStore;
  }
  activeStore = idbOutboundStore();
  return activeStore;
}

function setOutboundStore(store) {
  activeStore = store;
}

async function emitOutboundCount(store) {
  const rows = await store.all();
  const count = rows.length;
  if (typeof changeListener === 'function') changeListener(count);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent('pixie-outbound-changed', { detail: { count } }),
    );
  }
  return count;
}

async function deliverOrQueue({ online = true, item, send, store } = {}) {
  const db = store || outboundStore();
  const saved = normalizeOutbound(item);
  if (online === false) {
    await db.put(saved);
    await emitOutboundCount(db);
    return { queued: true, id: saved.id };
  }
  try {
    const result = await send();
    return { queued: false, result };
  } catch (error) {
    if (!isNetworkSendFailure(error, online)) throw error;
    await db.put(saved);
    await emitOutboundCount(db);
    return { queued: true, id: saved.id, error };
  }
}

async function blobToDataUrl(blob) {
  if (!blob) return '';
  if (typeof FileReader === 'function') {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(reader.error || new Error('read failed'));
      reader.readAsDataURL(blob);
    });
  }
  const bytes = Buffer.from(await blob.arrayBuffer());
  const type = blob.type || 'application/octet-stream';
  return `data:${type};base64,${bytes.toString('base64')}`;
}

async function postVoiceNote(item, post) {
  const form = new FormData();
  form.append('audioBlob', item.audio, item.filename || 'recording.ogg');
  form.append('title', item.title || 'Voice memo');
  if (item.attachedTo != null)
    form.append('attachedTo', String(item.attachedTo));
  if (item.attachedId != null)
    form.append('attachedId', String(item.attachedId));
  return post('/api/saveVoiceNote', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    skipGlobalError: true,
  });
}

async function postInsideChatMessage(item, post) {
  const audios = [];
  if (item.audio) {
    audios.push({
      mimeType: item.audioMime || item.audio.type || 'audio/ogg',
      data: await blobToDataUrl(item.audio),
    });
  }
  const images = item.images || [];
  if (item.isTenant) {
    const response = await post(
      '/api/tenant-chat/messages',
      {
        conversationId: item.conversationId,
        peopleId: item.peopleId,
        message: item.text || '',
        audios,
        attachPhotos: images.length > 0,
      },
      { skipGlobalError: true },
    );
    const message = response && response.data && response.data.message;
    return message && message.id;
  }
  const response = await post(
    '/api/chat-messages',
    {
      datetime: item.datetime,
      message: item.text || '',
      isReply: !!item.isReply,
      fromUserId: item.fromUserId,
      toUserId: item.toUserId,
      fromPeopleId: null,
      toPeopleId: item.toPeopleId,
      toGroupId: item.toGroupId,
      isReplyTo: item.isReplyTo,
      status: null,
      createdBy: item.fromUserId,
      images: [],
      audios,
      attachPhotos: images.length > 0,
    },
    { skipGlobalError: true },
  );
  const data = response && response.data && response.data.data;
  return data && data.id;
}

function fileFromStoredImage(image) {
  const blob = image.blob || image.file;
  const name = image.name || 'photo.jpg';
  const type = image.type || (blob && blob.type) || 'image/jpeg';
  if (typeof File === 'function' && blob) {
    try {
      return new File([blob], name, { type });
    } catch {
      return blob;
    }
  }
  return blob;
}

function uploadQueuedChatImage(uploadFile, messageId, image, kind) {
  return uploadFile(fileFromStoredImage(image), !!image.isHeic, {
    singleUrl: '/api/chat-images',
    fillSingleForm: (form) => {
      form.append('messageId', String(messageId));
      form.append('kind', kind);
    },
    initUrl: '/api/chat-images/chunked/init',
    initBody: { messageId, kind },
    chunkUrl: '/api/chat-images/chunked/upload',
    finalizeUrl: '/api/chat-images/chunked/finalize',
    skipGlobalError: true,
  });
}

function announceOutboundSent(detail) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('pixie-outbound-sent', { detail }));
}

async function forgetReplacedVoiceNote(item, response, put) {
  if (typeof put !== 'function' || item.replaceVoiceNoteId == null) return;
  const metadataId = response && response.data && response.data.metadataId;
  const previous = Number(item.replaceVoiceNoteId);
  if (!metadataId || !previous || previous === Number(metadataId)) return;
  try {
    await put(`/api/delete-voice-memo/${previous}`, null, {
      skipGlobalError: true,
    });
  } catch {
    // The new note is already saved. A missed delete can be cleaned up later.
  }
}

async function replayOutboundItem(item, { post, put, uploadImage } = {}) {
  if (item.kind === 'voice-note') {
    const response = await postVoiceNote(item, post);
    await forgetReplacedVoiceNote(item, response, put);
    announceOutboundSent({
      kind: 'voice-note',
      attachedTo: item.attachedTo,
      attachedId: item.attachedId,
    });
    return null;
  }
  if (item.kind !== 'inside-chat') return null;
  let messageId = item.messageId;
  if (messageId == null) {
    messageId = await postInsideChatMessage(item, post);
  }
  const images = item.images || [];
  let index = Number(item.imageIndex) || 0;
  const kind = item.isTenant ? 'tenant' : 'staff';
  while (index < images.length) {
    try {
      await uploadImage(messageId, images[index], kind);
    } catch (error) {
      const checkpoint = { ...item, messageId, imageIndex: index };
      if (isNetworkSendFailure(error, true)) {
        return { pending: true, item: checkpoint };
      }
      return { failed: true, item: checkpoint, error };
    }
    index += 1;
  }
  announceOutboundSent({
    kind: 'inside-chat',
    messageId,
    isTenant: !!item.isTenant,
    peopleId: item.peopleId,
    toUserId: item.toUserId,
    toGroupId: item.toGroupId,
    conversationId: item.conversationId,
  });
  return null;
}

async function flushOutbound({ store, online = true, replay } = {}) {
  const db = store || outboundStore();
  const items = (await db.all()).slice().sort((a, b) => {
    return (a.createdAt || 0) - (b.createdAt || 0);
  });
  if (online === false) {
    return { sent: 0, pending: items.length, stopped: true };
  }
  let sent = 0;
  for (const item of items) {
    try {
      const next = await replay(item);
      if (next && next.pending) {
        await db.put(normalizeOutbound(next.item));
        return { sent, pending: items.length - sent, stopped: true };
      }
      if (next && next.failed) {
        const failedItem = normalizeOutbound(next.item);
        const failures = (failedItem.failures || 0) + 1;
        if (failures >= 3) {
          await db.delete(failedItem.id);
          continue;
        }
        await db.put({ ...failedItem, failures });
        continue;
      }
      await db.delete(item.id);
      sent += 1;
    } catch (error) {
      if (isNetworkSendFailure(error, true)) {
        return { sent, pending: items.length - sent, stopped: true };
      }
      const failures = (item.failures || 0) + 1;
      if (failures >= 3) {
        await db.delete(item.id);
        continue;
      }
      await db.put({ ...item, failures });
    }
  }
  const left = await db.all();
  return { sent, pending: left.length, stopped: false };
}

function installOutboundQueue({
  post,
  put,
  uploadFile,
  isOnline,
  onChange,
} = {}) {
  changeListener = onChange || null;
  const store = outboundStore();
  let flushing = false;
  const flush = async () => {
    if (flushing) return;
    const online = typeof isOnline === 'function' ? isOnline() !== false : true;
    if (!online) {
      await emitOutboundCount(store);
      return;
    }
    flushing = true;
    try {
      await flushOutbound({
        store,
        online: true,
        replay: (item) =>
          replayOutboundItem(item, {
            post,
            put,
            uploadImage: (messageId, image, kind) =>
              uploadQueuedChatImage(uploadFile, messageId, image, kind),
          }),
      });
    } finally {
      flushing = false;
      await emitOutboundCount(store);
    }
  };
  const onOnline = () => {
    flush();
  };
  const onVisible = () => {
    if (
      typeof document === 'undefined' ||
      document.visibilityState === 'visible'
    ) {
      flush();
    }
  };
  if (typeof window !== 'undefined') {
    window.addEventListener('online', onOnline);
    window.addEventListener('pixie-outbound-flush', onOnline);
  }
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisible);
  }
  flush();
  return {
    flush,
    stop() {
      if (typeof window !== 'undefined') {
        window.removeEventListener('online', onOnline);
        window.removeEventListener('pixie-outbound-flush', onOnline);
      }
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisible);
      }
      if (changeListener === onChange) changeListener = null;
    },
  };
}

module.exports = {
  OUTBOUND_HOLD_TEXT,
  isNetworkSendFailure,
  normalizeOutbound,
  insideChatOutboundItem,
  memoryOutboundStore,
  setOutboundStore,
  deliverOrQueue,
  flushOutbound,
  replayOutboundItem,
  installOutboundQueue,
  blobToDataUrl,
};
